import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import { none } from '@remotion/transitions/none';
import { linearTiming, TransitionTiming } from '@remotion/transitions';
import { TransitionType } from '../../../../types/scene';

export type RemotionPresentation = ReturnType<typeof fade> | ReturnType<typeof slide> | ReturnType<typeof wipe> | ReturnType<typeof none>;

export interface MappedTransition {
  presentation: RemotionPresentation;
  timing: TransitionTiming;
  durationFrames: number;
}

export function mapTransitionType(
  type: TransitionType,
  durationSeconds: number,
  fps: number
): MappedTransition {
  const durationFrames = Math.max(1, Math.round(durationSeconds * fps));
  const timing = linearTiming({ durationInFrames: durationFrames });

  switch (type) {
    case 'cut':
      return {
        presentation: none(),
        timing: linearTiming({ durationInFrames: 1 }),
        durationFrames: 1,
      };

    case 'fade':
      return {
        presentation: fade({ shouldFadeOutExitingScene: true }),
        timing,
        durationFrames,
      };

    case 'crossfade':
      return {
        presentation: fade({ shouldFadeOutExitingScene: true }),
        timing,
        durationFrames,
      };

    case 'slide':
      return {
        presentation: slide({ direction: 'from-right' }),
        timing,
        durationFrames,
      };

    default:
      return {
        presentation: none(),
        timing: linearTiming({ durationInFrames: 1 }),
        durationFrames: 1,
      };
  }
}

export function calculateTransitionDurations(
  fromDurationMs: number,
  toDurationMs: number,
  transitionDurationSeconds: number,
  clipDurationSeconds: number,
  fps: number
): {
  fromClipDurationMs: number;
  toClipDurationMs: number;
  transitionDurationFrames: number;
  totalDurationFrames: number;
} {
  const fromClipDurationMs = Math.min(clipDurationSeconds * 1000, fromDurationMs);
  const toClipDurationMs = Math.min(clipDurationSeconds * 1000, toDurationMs);
  
  const maxTransitionMs = Math.min(fromClipDurationMs, toClipDurationMs);
  const actualTransitionMs = Math.min(transitionDurationSeconds * 1000, maxTransitionMs * 0.9);
  
  const transitionDurationFrames = Math.max(1, Math.round((actualTransitionMs / 1000) * fps));
  
  const fromClipFrames = Math.ceil((fromClipDurationMs / 1000) * fps);
  const toClipFrames = Math.ceil((toClipDurationMs / 1000) * fps);
  
  const totalDurationFrames = fromClipFrames + toClipFrames - transitionDurationFrames;

  return {
    fromClipDurationMs,
    toClipDurationMs,
    transitionDurationFrames,
    totalDurationFrames,
  };
}
